/**
 * @lines98/client/lib/game-session-heartbeat — sliding-TTL liveness orchestration for
 * the active all-time session (RP-5 / run-persistence-lifecycle.md T-B §5.3).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (addendum-001 Решение 2 Option A): чтобы «Pro играл доску
 *   днями», серверная сессия должна продлеваться по активности (sliding-TTL). Этот
 *   модуль — ОДНА «ручка» (НЕ дубль по экранам — code-structure §1/§3): board-screen
 *   дёргает её при сохранении прогресса (троттл `SESSION_HEARTBEAT_THROTTLE_MIN`),
 *   resume — при возобновлении (без троттла — должно долететь сразу). Один источник
 *   троттл-окна + один обработчик «партия истекла» (clear cache + тост + лобби, AC-B.6).
 *
 * НЕ путать с `lib/session-heartbeat.ts` — ТО liveness-пинг ОТОЗВАННОГО токена
 *   (multi-browser revoke). ЭТО — TTL-продление ИГРОВОЙ сессии. Разные концепции.
 *
 * SCOPE: троттл + POST /game/heartbeat + обработка «истекла» (только all_time —
 *   casual сессии не имеет). На save — best-effort троттл; на resume — немедленный
 *   пинг + если истекла, мягкий выход в лобби.
 * NOT IN SCOPE: сам POST (services/game-heartbeat); запись прогресса (in-progress-cache);
 *   resume-реплей (run-resume); daily.
 *
 * Cross-references:
 *   - run-persistence-lifecycle.md §5.3 (T-B «играть днями») + §6 Edge (TTL истёк).
 *   - services/game-heartbeat.ts (POST wrapper); lib/min-interval-gate.ts (троттл).
 *   - lib/in-progress-cache.ts (clearRun на истечении); store/game-store.ts (лобби).
 */

import { SESSION_HEARTBEAT_THROTTLE_MIN } from '@lines98/shared/config/balance';

import { pingHeartbeat, type HeartbeatResult } from '../services/game-heartbeat.js';
import { useGameStore } from '../store/game-store.js';
import { showToast } from '../ui/toast.js';

import { clearRun } from './in-progress-cache.js';
import { createMinIntervalGate } from './min-interval-gate.js';
import { t } from './i18n/index.js';

/**
 * Троттл-окно для save-пингов: ОДИН гейт на весь модуль (одна активная партия за раз).
 * `SESSION_HEARTBEAT_THROTTLE_MIN` минут → мс. Resume-пинг троттл НЕ проходит (см. ниже).
 */
const saveGate = createMinIntervalGate(SESSION_HEARTBEAT_THROTTLE_MIN * 60_000);

/**
 * Обработать истечение серверной сессии (AC-B.6): почистить кэш активной партии,
 * показать понятный тост (НЕ молчаливая подмена seed — I-1) и увести в лобби. Идём в
 * лобби только если игрок всё ещё на этой (теперь мёртвой) партии — не дёргаем UI,
 * если он уже ушёл сам. Fire-and-forget.
 */
function handleExpiredRun(): void {
  void clearRun();
  // Тост строится в момент истечения (показа) → t() = актуальный язык (не запекаем
  // на import: module-const запекся бы на дефолтном языке до boot-локали).
  showToast(t('game.session.runExpired'));
  const s = useGameStore.getState();
  // Только если игрок ещё в живой не-завершённой партии на доске (не на итогах/лобби).
  if (s.phase === 'board' && !s.finishReason) {
    useGameStore.getState().showModeSelect();
  }
}

/**
 * Heartbeat при СОХРАНЕНИИ прогресса (board-screen, после хода). Троттлится
 * (`SESSION_HEARTBEAT_THROTTLE_MIN`) — на 72h idle-TTL чаще не нужно. Best-effort:
 * сетевой сбой проглатывается; на 404 (истекла) — мягкий выход (handleExpiredRun).
 * Fire-and-forget (не возвращает промис вызывающему — он не ждёт).
 */
export function heartbeatOnSave(sessionId: string, now: number = Date.now()): void {
  if (!saveGate.allow(now)) return; // слишком часто — пропуск
  void pingHeartbeat(sessionId).then((result) => onResult(result));
}

/**
 * Heartbeat при ВОЗОБНОВЛЕНИИ из кэша (resume). НЕ троттлится — должен долететь сразу,
 * чтобы продлить, возможно, почти-истёкшую сессию ДО продолжения игры. Возвращает
 * результат, чтобы вызывающий (resume) мог решить (но истечение уже обработано здесь —
 * handleExpiredRun чистит + ведёт в лобби). Сетевой сбой — benign (играем дальше; finish
 * на завершении всё равно сверит сессию).
 */
export async function heartbeatOnResume(sessionId: string): Promise<HeartbeatResult> {
  const result = await pingHeartbeat(sessionId);
  onResult(result);
  return result;
}

/** Единая реакция на результат пинга — истечение лечим, остальное best-effort. */
function onResult(result: HeartbeatResult): void {
  if (result.status === 'expired') handleExpiredRun();
  // 'ok' / 'network' — ничего (продление прошло / сбой benign).
}
